import { Seo } from "../components/Seo";
import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  AlertTriangle,
  Layers,
  ChevronRight,
  Sparkles,
  FileText,
  Clock,
  Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PageHero } from "../components/PageHero";
import { VG_ORANGE, VG_CHARCOAL } from "../lib/brand";
import { ENVIRONMENTS, RESOURCE_CATEGORIES, ENV_GALLERIES } from "../data/solutions";

const IMG_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='100%' height='100%' fill='#e7e5e4'/><text x='50%' y='50%' font-family='sans-serif' font-size='16' fill='#9c9a97' text-anchor='middle' dominant-baseline='middle'>Image coming soon</text></svg>`
  );

const onImgError = (e) => {
  if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK;
};

const productHref = (col, slug) =>
  col.product ? `/products/${col.product}?from=solutions&env=${slug}` : "/products";

export default function Solutions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const envParam = searchParams.get("env");
  const explorerRef = useRef(null);
  const didInitScroll = useRef(false);

  const [activeSlug, setActiveSlug] = useState(
    () => ENVIRONMENTS.find((e) => e.slug === envParam)?.slug || ENVIRONMENTS[0].slug
  );
  const [activeCollection, setActiveCollection] = useState(null);
  const env = ENVIRONMENTS.find((e) => e.slug === activeSlug) || ENVIRONMENTS[0];
  const galleryImages = ENV_GALLERIES[env.slug] || env.collections.map((c) => c.image);

  // Sync active environment when the ?env= param changes (e.g. via nav dropdown).
  useEffect(() => {
    if (envParam && ENVIRONMENTS.some((e) => e.slug === envParam) && envParam !== activeSlug) {
      setActiveSlug(envParam);
      setActiveCollection(null);
    }
  }, [envParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // On first load via a deep link, scroll the explorer into view.
  useEffect(() => {
    if (!didInitScroll.current && envParam && explorerRef.current) {
      didInitScroll.current = true;
      explorerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [envParam]);

  const selectEnvironment = (slug) => {
    setActiveSlug(slug);
    setActiveCollection(null);
    setSearchParams({ env: slug });
  };

  return (
    <div data-testid="solutions-page">
      <Seo title="Solutions" description="See van Gelder commercial flooring and matting solutions in real projects across corporate, retail, healthcare, education, hospitality, and industrial environments." />
      <PageHero
        title="Solutions for Every Environment"
        subtitle="Explore how van Gelder pairs the right flooring systems to the demands of your space — from entrance to interior, back-of-house to boardroom."
        testId="solutions-hero"
      />

      {/* Explorer: sidebar + content panel */}
      <section ref={explorerRef} className="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div className="mb-8">
          <div className="h-1 w-16 mb-4" style={{ backgroundColor: VG_ORANGE }} />
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-neutral-900">
            Explore by Environment
          </h2>
          <p className="mt-2 max-w-2xl text-neutral-600">
            Select an environment to see its priorities, common challenges, recommended flooring
            systems, and curated product collections.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="environment-list">
            <ul className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {ENVIRONMENTS.map((e) => {
                const active = e.slug === activeSlug;
                return (
                  <li key={e.slug} className="shrink-0 lg:shrink">
                    <button
                      data-testid={`environment-tab-${e.slug}`}
                      onClick={() => selectEnvironment(e.slug)}
                      className={`group w-full text-left rounded-lg border px-4 py-3 transition-colors flex items-center justify-between gap-2 whitespace-nowrap lg:whitespace-normal ${
                        active
                          ? "border-transparent text-white"
                          : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
                      }`}
                      style={active ? { backgroundColor: VG_ORANGE } : undefined}
                    >
                      <span className="font-heading font-semibold text-sm">{e.name}</span>
                      <ChevronRight
                        className={`h-4 w-4 shrink-0 transition-transform ${
                          active ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Content panel */}
          <div key={env.slug} data-testid="environment-content" className="min-w-0">
            {/* Banner */}
            <div className="relative overflow-hidden rounded-xl bg-[#307fbf]">
              <img
                src={env.image}
                alt={env.name}
                onError={onImgError}
                className="absolute inset-0 h-full w-full object-cover opacity-35"
              />
              <div className="relative px-6 py-10 md:px-10 md:py-14">
                <span className="inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur">
                  Environment
                </span>
                <h3
                  className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-white"
                  data-testid="environment-title"
                >
                  {env.name}
                </h3>
                <p className="mt-3 max-w-2xl text-neutral-100 leading-relaxed">{env.overview}</p>
              </div>
            </div>

            {/* Project gallery strip */}
            {galleryImages.length > 0 && (
              <div className="mt-6" data-testid="project-gallery">
                <div className="flex items-center gap-2 mb-3">
                  <Camera className="h-4 w-4" style={{ color: VG_ORANGE }} />
                  <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-neutral-700">
                    See it in a real project
                  </h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {galleryImages.slice(0, 4).map((src, i) => (
                    <div
                      key={i}
                      data-testid={`gallery-photo-${i}`}
                      className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100"
                    >
                      <img
                        src={src}
                        alt={`${env.name} installation ${i + 1}`}
                        onError={onImgError}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Priorities + Challenges */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl border border-neutral-200 bg-white p-6">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5" style={{ color: VG_ORANGE }} />
                  <h4 className="font-heading font-bold text-neutral-900">Design Priorities</h4>
                </div>
                <ul className="mt-4 space-y-3">
                  {env.priorities.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sm text-neutral-700">
                      <span
                        className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: VG_ORANGE }}
                      />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-6">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-neutral-500" />
                  <h4 className="font-heading font-bold text-neutral-900">Common Challenges</h4>
                </div>
                <ul className="mt-4 space-y-3">
                  {env.challenges.map((ch, i) => (
                    <li key={i} className="flex gap-3 text-sm text-neutral-700">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
                      {ch}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Recommended systems */}
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="h-5 w-5" style={{ color: VG_ORANGE }} />
                <h4 className="font-heading font-bold text-neutral-900">
                  Recommended Flooring Systems
                </h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {env.recommended.map((r, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-neutral-200 bg-white px-4 py-1.5 text-sm font-medium text-neutral-700"
                    data-testid="recommended-system"
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>

            {/* Collection cards */}
            <div className="mt-10">
              <h4 className="font-heading font-bold text-neutral-900 mb-4">
                Curated Collections
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {env.collections.map((col, i) => (
                  <div
                    key={i}
                    data-testid={`collection-card-${i}`}
                    className="group rounded-xl border border-neutral-200 bg-white overflow-hidden hover:shadow-lg hover:border-neutral-300 transition-all flex flex-col"
                  >
                    <button
                      onClick={() => setActiveCollection(col)}
                      data-testid={`collection-card-image-${i}`}
                      className="block text-left"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                        <img
                          src={col.image}
                          alt={col.name}
                          onError={onImgError}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </button>
                    <div className="p-5 flex flex-col flex-1">
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: VG_ORANGE }}
                      >
                        {col.system}
                      </span>
                      <h5 className="mt-1 font-heading font-bold text-neutral-900">{col.name}</h5>
                      <p className="mt-1 text-sm font-medium text-neutral-500">{col.application}</p>
                      <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{col.benefit}</p>
                      <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center justify-between gap-3">
                        <button
                          onClick={() => setActiveCollection(col)}
                          data-testid={`collection-details-btn-${i}`}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 hover:text-neutral-800 transition-colors"
                        >
                          View details
                        </button>
                        {col.product && (
                          <Link
                            to={productHref(col, env.slug)}
                            data-testid={`collection-product-link-${i}`}
                            className="inline-flex items-center gap-1 text-sm font-semibold hover:opacity-80 transition-opacity"
                            style={{ color: VG_ORANGE }}
                          >
                            View product
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Outcomes */}
            <div className="mt-10 rounded-xl border border-neutral-200 p-6 md:p-8" style={{ backgroundColor: "#faf7f2" }}>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5" style={{ color: VG_ORANGE }} />
                <h4 className="font-heading font-bold text-neutral-900">Expected Outcomes</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {env.outcomes.map((o, i) => (
                  <div key={i} className="flex gap-3 text-sm text-neutral-800">
                    <Check className="h-5 w-5 shrink-0" style={{ color: VG_ORANGE }} />
                    <span>{o}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button asChild className="text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }} data-testid="solutions-products-cta">
                  <Link to="/products">Explore Products</Link>
                </Button>
                <Button asChild variant="outline" data-testid="solutions-contact-cta">
                  <Link to="/contact">Talk to a Specialist</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Resource Center */}
      <section className="bg-neutral-50 border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
          <div className="max-w-2xl">
            <div className="h-1 w-16 mb-4" style={{ backgroundColor: VG_ORANGE }} />
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-neutral-900">
              Design Resource Center
            </h2>
            <p className="mt-2 text-neutral-600">
              Specification tools, technical documentation, and design assets to support your
              project — expanding as our new platform comes online.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RESOURCE_CATEGORIES.map((r, i) => (
              <div
                key={i}
                data-testid={`resource-category-${i}`}
                className="rounded-xl border border-neutral-200 bg-white p-5 flex items-start gap-4"
              >
                <div className="shrink-0 rounded-lg bg-neutral-100 p-2.5">
                  <FileText className="h-5 w-5 text-neutral-500" />
                </div>
                <div className="min-w-0">
                  <h5 className="font-heading font-semibold text-neutral-900">{r.name}</h5>
                  <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500">
                    <Clock className="h-3.5 w-3.5" />
                    {r.note}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collection detail modal */}
      <Dialog open={!!activeCollection} onOpenChange={(o) => !o && setActiveCollection(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="collection-modal">
          {activeCollection && (
            <>
              <div className="-mx-6 -mt-6 mb-4 h-52 sm:h-56 shrink-0 overflow-hidden bg-neutral-100">
                <img
                  src={activeCollection.image}
                  alt={activeCollection.name}
                  onError={onImgError}
                  className="h-full w-full object-cover"
                />
              </div>
              <DialogHeader>
                <span
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: VG_ORANGE }}
                >
                  {activeCollection.system}
                </span>
                <DialogTitle className="font-heading text-2xl">
                  {activeCollection.name}
                </DialogTitle>
                <DialogDescription className="text-neutral-600">
                  {activeCollection.application} — {activeCollection.benefit}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-2">
                <ModalBlock title="Why it fits">
                  <p className="text-sm text-neutral-700 leading-relaxed">{activeCollection.fit}</p>
                </ModalBlock>

                <ModalBlock title="Typical applications">
                  <div className="flex flex-wrap gap-2">
                    {activeCollection.applications.map((a, i) => (
                      <span
                        key={i}
                        className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </ModalBlock>

                <ModalBlock title="Coordinating systems">
                  <div className="flex flex-wrap gap-2">
                    {activeCollection.coordinating.map((a, i) => (
                      <span
                        key={i}
                        className="rounded-full border border-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </ModalBlock>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <ModalBlock title="Transitions">
                    <p className="text-sm text-neutral-700 leading-relaxed">
                      {activeCollection.transitions}
                    </p>
                  </ModalBlock>
                  <ModalBlock title="Maintenance">
                    <p className="text-sm text-neutral-700 leading-relaxed">
                      {activeCollection.maintenance}
                    </p>
                  </ModalBlock>
                </div>

                <ModalBlock title="Resources">
                  <ul className="space-y-2">
                    {activeCollection.resources.map((r, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-neutral-500">
                        <Clock className="h-3.5 w-3.5" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </ModalBlock>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild className="text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }} data-testid="modal-products-cta">
                    <Link to={activeCollection.product ? productHref(activeCollection, env.slug) : "/products"}>
                      {activeCollection.product ? "View Product Details" : "Browse Products"}
                    </Link>
                  </Button>
                  <Button asChild variant="outline" data-testid="modal-contact-cta">
                    <Link to="/contact">Request Information</Link>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ModalBlock = ({ title, children }) => (
  <div>
    <h6
      className="mb-2 text-xs font-bold uppercase tracking-wide"
      style={{ color: VG_CHARCOAL }}
    >
      {title}
    </h6>
    {children}
  </div>
);
